import React, { useEffect, useRef } from 'react';
import { Play, Pause, RotateCcw } from 'lucide-react';
import { useLLDPractice } from '../../context/LLDPracticeContext';
import { formatTime, isUrgent } from '../../utils/timerLogic';

/**
 * TimerComponent — Countdown timer for Interview Mode.
 *
 * Features:
 * - Countdown display in MM:SS format
 * - Urgency indicator (red + pulse) when ≤ 5 minutes remain
 * - Progress ring showing elapsed time percentage
 * - Dispatches TICK_TIMER every second via useEffect interval
 * - Sticky top-right positioning
 * - Only renders when timer is not null (Interview Mode)
 */
const TimerComponent: React.FC = () => {
   const { state, dispatch } = useLLDPractice();
   const { timer } = state.sessionState;
   const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

   // Set up the 1-second tick interval
   useEffect(() => {
      if (!timer || !timer.isRunning) {
         // Clear interval if timer is paused or null
         if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
         }
         return;
      }

      intervalRef.current = setInterval(() => {
         dispatch({ type: 'TICK_TIMER' });
      }, 1000);

      return () => {
         if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
         }
      };
   }, [timer?.isRunning, dispatch]);

   // Don't render if no timer (not in Interview Mode)
   if (!timer) {
      return null;
   }

   const { totalSeconds, remainingSeconds } = timer;
   const urgent = isUrgent(remainingSeconds);
   const elapsedFraction = totalSeconds > 0 ? (totalSeconds - remainingSeconds) / totalSeconds : 0;
   const remainingFraction = 1 - elapsedFraction;
   const expired = remainingSeconds === 0;

   // Progress-ring colour shifts as time runs down: blue → amber → rose.
   const ringColor = urgent ? '#f43f5e' : remainingFraction <= 0.5 ? '#f59e0b' : '#3b82f6';

   // Compact mini ring so the pill height matches the adjacent "Interview
   // Mode" badge (single row: px-3 py-1.5 text-sm rounded-lg).
   const size = 20;
   const strokeWidth = 3;
   const radius = (size - strokeWidth) / 2;
   const circumference = 2 * Math.PI * radius;
   // Draw the ELAPSED (completed) portion so the ring visibly FILLS as time
   // passes — empty at the start, full when time's up. The faint track behind
   // it shows the remaining (pending) portion.
   const strokeDashoffset = circumference * remainingFraction;

   return (
      <div
         role="timer"
         aria-label={`Time remaining: ${formatTime(remainingSeconds)} (${timer.isRunning ? 'running' : expired ? "time's up" : 'paused'})`}
         aria-live="polite"
         className={`
        inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-semibold ring-1
        select-none transition-colors
        ${urgent
               ? 'bg-rose-50 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 ring-rose-200 dark:ring-rose-800'
               : 'bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-200 ring-gray-200 dark:ring-gray-700'
            }
      `}
      >
         {/* Mini progress ring */}
         <span className="relative shrink-0" style={{ width: size, height: size }}>
            <svg width={size} height={size} className="block -rotate-90" aria-hidden="true">
               <circle
                  cx={size / 2}
                  cy={size / 2}
                  r={radius}
                  fill="none"
                  stroke="currentColor"
                  className="text-gray-300 dark:text-gray-600"
                  strokeWidth={strokeWidth}
               />
               <circle
                  cx={size / 2}
                  cy={size / 2}
                  r={radius}
                  fill="none"
                  stroke={ringColor}
                  strokeWidth={strokeWidth}
                  strokeDasharray={circumference}
                  strokeDashoffset={strokeDashoffset}
                  strokeLinecap="round"
                  className="transition-all duration-1000 ease-linear"
               />
            </svg>
         </span>

         {/* Time */}
         <span className={`font-mono tabular-nums tracking-wide ${urgent ? 'animate-pulse' : ''}`}>
            {formatTime(remainingSeconds)}
         </span>

         {/* Divider */}
         <span className="h-4 w-px bg-current opacity-15" aria-hidden="true" />

         {/* Controls: start/pause + reset (icon-only, keep the row compact) */}
         <button
            type="button"
            onClick={() => dispatch({ type: 'TOGGLE_TIMER' })}
            disabled={expired}
            aria-label={timer.isRunning ? 'Pause timer' : 'Start timer'}
            title={timer.isRunning ? 'Pause' : 'Start'}
            className="inline-flex items-center justify-center rounded-md p-1 -my-1 hover:bg-black/5 dark:hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
         >
            {timer.isRunning ? (
               <Pause className="w-4 h-4" aria-hidden="true" />
            ) : (
               <Play className="w-4 h-4" aria-hidden="true" />
            )}
         </button>
         <button
            type="button"
            onClick={() => dispatch({ type: 'RESET_TIMER' })}
            aria-label="Reset timer"
            title="Reset"
            className="inline-flex items-center justify-center rounded-md p-1 -my-1 hover:bg-black/5 dark:hover:bg-white/10 transition-colors"
         >
            <RotateCcw className="w-4 h-4" aria-hidden="true" />
         </button>
      </div>
   );
};

export default TimerComponent;
